// Configuración del runtime, leída de /config.json al arrancar.
//
// Es la pieza que permite que el MISMO bundle sirva para los tres runtimes:
// en la web lo sirve nginx, en el ejecutable de la caja lo sirve Kestrel local
// con la URL local y sus capacidades de hardware, y en el APK es un archivo
// estático del bundle.
//
// Si estuviera horneado en build-time habría tres builds y tres versiones que
// mantener, y el OTA tendría que elegir cuál mandar a cada cliente.

export type Runtime = 'web' | 'caja' | 'movil'

export interface Capacidades {
  /** Peso en vivo por puerto serie. Solo el ejecutable de la caja. */
  balanza: boolean
  /** Impresión térmica directa. La web imprime por CSS. */
  impresora: boolean
  /** Apertura del cajón por comando de la impresora. */
  cajon: boolean
  /** Lector de códigos de barras conectado. */
  lector: boolean
  /** Si se puede seguir vendiendo sin internet. */
  offline: boolean
}

export interface ConfigRuntime {
  apiUrl: string
  runtime: Runtime
  capacidades: Capacidades
  /** Versión de la UI en ejecución. La sirve el host, no el bundle. */
  version?: string

  /**
   * Ancho en caracteres de la térmica CONFIGURADA en esta máquina.
   *
   * Lo informa el host de la caja para que el reporte en papel de ticket salga
   * con el ancho de la impresora que ya se probó desde la pantalla del equipo,
   * en lugar de uno adivinado. En el navegador no viene: ahí no hay ninguna
   * térmica configurada y la pantalla deja elegirlo.
   */
  anchoTicket?: number
}

const SIN_CAPACIDADES: Capacidades = {
  balanza: false,
  impresora: false,
  cajon: false,
  lector: false,
  offline: false,
}

// Valor por defecto para el dev server, donde no hay nadie que sirva
// config.json y /api va por el proxy de Vite.
const POR_DEFECTO: ConfigRuntime = {
  apiUrl: '/api',
  runtime: 'web',
  capacidades: SIN_CAPACIDADES,
}

let actual: ConfigRuntime = POR_DEFECTO

export function config(): ConfigRuntime {
  return actual
}

export async function cargarConfig(): Promise<ConfigRuntime> {
  try {
    // Se pide al PROPIO origen y sin caché: si se cacheara, cambiar la URL de
    // la API obligaría a que cada navegador borre su caché.
    const res = await fetch('/config.json', { cache: 'no-store' })

    if (res.ok) {
      const json = (await res.json()) as Partial<ConfigRuntime>

      actual = {
        apiUrl: json.apiUrl ?? POR_DEFECTO.apiUrl,
        runtime: json.runtime ?? POR_DEFECTO.runtime,
        capacidades: { ...SIN_CAPACIDADES, ...(json.capacidades ?? {}) },
        version: json.version,
      }
    }
  } catch {
    // Sin config.json se sigue con los valores por defecto. Es el caso normal
    // del dev server, y en producción un fallo acá no debe impedir que la
    // pantalla cargue: se degrada a la configuración de la web.
  }

  return actual
}

/** Atajo legible: `esCaja()` en lugar de comparar strings por todos lados. */
export const esCaja = () => actual.runtime === 'caja'
export const esMovil = () => actual.runtime === 'movil'
